import { Color } from "colorwee";
import {
    CanvasBoard,
    CanvasElement,
    CanvasLayer,
    ImageElement,
    ImageSourceElement,
    ImgUtils,
    MathX,
    Point,
    PolygonElement,
    PolygonHitType,
    Rect,
    ShapeStyle
} from "mini-canvas-engine";
import { DotsConnector } from "../game/dots-connector";
import { DOTS_EDITOR_DOT_RADIUS, DOT_HIT_EXPANSION, DOT_HIT_EXPANSION_TOUCH, MAX_SCALE } from "../game/game-consts";
import { GameImageGenerator } from "../generator/generator-types";

export class DotsEditor implements GameImageGenerator {
    board: CanvasBoard;
    backgroundElem: ImageElement;
    hintElem: ImageElement;
    dotsLayer: CanvasLayer;
    polygon: PolygonElement;
    points: Point[];
    ratio: number;
    boundsRect: Rect;
    margin: number;
    changed = false;
    onCanDeletePoint: (index: number) => boolean;
    onPointDeleted: (index: number) => void;
    onCanAddPoint: (index: number) => boolean;
    onPointAdded: (index: number) => void;
    onFinishedPointEdit: (index: number) => void;

    componentPosOnDragStart: Point;
    manuallyEdited: boolean;

    constructor(public container: HTMLElement, public minPointDistance: number, public maxImageSize = 1200) {
        this.board = new CanvasBoard(container);
        this.board.transform.scale = 1;
        this.board.setTransformBase(MAX_SCALE * this.board.pixelRatio);
        this.backgroundElem = new ImageElement(false, "background");
        this.board.layers[0].add(this.backgroundElem);

        this.hintElem = new ImageElement(false, "hint");
        this.board.addLayer("hint").add(this.hintElem);

        this.dotsLayer = this.board.addLayer("dots");
        const polygonShapeStyle = new ShapeStyle(
            new Color(0, 0, 0),
            undefined,
            Math.round(2 * this.board.pixelRatio),
            "round",
            "round",
            Math.round(3 * this.board.pixelRatio),
            new Color(255, 255, 255)
        );
        if (this.board.pixelRatio > 1) {
            // Mobile device. Simplify line shadow
            polygonShapeStyle.lineShadowSize = Math.round(2 * this.board.pixelRatio);
            polygonShapeStyle.lineShadowUseAlpha = false;
        }
        this._dotRadius = DOTS_EDITOR_DOT_RADIUS;
        this.polygon = new PolygonElement(
            [],
            polygonShapeStyle,
            new ShapeStyle(new Color(0, 0, 0), new Color(255, 255, 255), Math.round(this.board.pixelRatio)),
            Math.round(this._dotRadius * this.board.pixelRatio),
            true,
            false,
            "path"
        );
        this.polygon.hitExpansion = DOT_HIT_EXPANSION * this.board.pixelRatio;
        this.dotsLayer.add(this.polygon);

        this.resetMouseProperties(true);
        this.board.onMouseDown = (pos) => this.boardMouseDown(pos);
        this.board.onMouseMove = (pos) => this.boardMouseMove(pos);
        this.board.onMouseUp = (pos, e, hitElement, isClick) => this.boardMouseUp(pos, e, hitElement, isClick);
        this.board.onMouseLeave = (pos, e) => this.boardMouseUp(pos, e, undefined, false);

        // Start the font loading in the very beginning
        DotsConnector.loadFonts();
    }

    _dotRadius: number;
    get dotRadius(): number {
        return this._dotRadius;
    }

    set dotRadius(value: number) {
        if (value != this._dotRadius) {
            this._dotRadius = value;
            this.polygon.dotRadius = Math.round(this._dotRadius * this.board.pixelRatio);
        }
    }

    clearImage() {
        this.backgroundElem.image = undefined;
        this.hintElem.image = undefined;
        this.setPoints([], this.ratio, this.margin);
        this.board.stop();
        this.changed = false;
        this.manuallyEdited = false;
    }

    getSourceImage(): ImageSourceElement | undefined {
        return this.backgroundElem.image;
    }

    loadSourceImage(url: string | ArrayBuffer): Promise<void> {
        return ImgUtils.loadImageOnCanvas(url, this.maxImageSize, true).then((canvas) => {
            this.backgroundElem.image = canvas;
            this.setCanvasBoardDimensions();
            this.changed = false;
            this.manuallyEdited = false;
        });
    }

    setHintImage(canvas: HTMLCanvasElement) {
        this.hintElem.image = canvas;
        this.hintElem.width = this.board.layers[0].width;
        this.hintElem.height = this.board.layers[0].height;
        this.changed = !!canvas || this.changed;
    }

    setCanvasBoardDimensions() {
        const parent = this.container.parentElement;
        if (!parent) {
            throw new Error("Parent element not set");
        }
        if (!this.backgroundElem.image) {
            throw new Error("Background image not set");
        }

        // https://stackoverflow.com/questions/25197184/get-the-height-of-an-element-minus-padding-margin-border-widths
        const computedStyle = window.getComputedStyle(parent, null);
        let parentWidth = parseInt(computedStyle.getPropertyValue("width"));
        let parentHeight = parseInt(computedStyle.getPropertyValue("height"));
        if (computedStyle.getPropertyValue("box-sizing") != "content-box") {
            parentHeight -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
            parentWidth -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
        }

        const imageWidth = this.backgroundElem.image.width;
        const imageHeight = this.backgroundElem.image.height;

        let ratio: number;
        if (imageWidth > parentWidth || imageHeight > parentHeight) {
            ratio = Math.min(parentWidth / imageWidth, parentHeight / imageHeight);
        } else {
            ratio = 1;
        }
        this.container.style.width = ratio * imageWidth + "px";
        this.container.style.height = ratio * imageHeight + "px";
        this.board.initializeCanvasSizes();
        this.board.setTransformBase(MAX_SCALE * this.board.pixelRatio);
        this.backgroundElem.width = this.board.layers[0].width;
        this.backgroundElem.height = this.board.layers[0].height;
        this.board.start();
    }

    getGameImageAsDataUrl(mimeType?: string): string {
        this.board.redrawAll = true;
        this.board.draw(false);
        return this.board.layers[0].toDataUrl(mimeType);
    }

    setPoints(points: Point[], ratio: number, margin: number) {
        this.points = [];
        this.ratio = ratio;
        this.margin = margin;
        this.boundsRect = new Rect(margin, margin, this.backgroundElem.width / ratio - 2 * margin, this.backgroundElem.height / ratio - 2 * margin);
        this.polygon.clear();
        for (const p of points) {
            const x = MathX.round(Math.min(Math.max(p.x, this.boundsRect.x), this.boundsRect.right), 1);
            const y = MathX.round(Math.min(Math.max(p.y, this.boundsRect.y), this.boundsRect.bottom), 1);
            this.points.push(new Point(x, y));
            const translatedPoint = new Point(x * ratio, y * ratio);
            this.polygon.addPoint(translatedPoint);
        }
    }

    /* MOUSE handlers */

    hitComp?: { hitType: PolygonHitType; index: number; distance: number };
    startDragPos?: Point;
    startDragTime: number;
    lastDotClickIndex: number;
    lastDotClickTime: number;
    triggeredDrag: boolean;

    resetMouseProperties(resetClick: boolean) {
        this.hitComp = undefined;
        this.startDragPos = undefined;
        this.startDragTime = 0;
        this.triggeredDrag = false;
        if (resetClick) {
            this.lastDotClickIndex = -1;
            this.lastDotClickTime = 0;
        }
    }

    boardMouseDown(pos: Point): boolean {
        const hitComp = this.polygon.getHitComponent(pos, DOT_HIT_EXPANSION_TOUCH);
        if (!hitComp || hitComp.hitType == PolygonHitType.None) {
            this.resetMouseProperties(true);
            return false;
        }
        this.hitComp = { hitType: hitComp.hitType, index: hitComp.index ?? 0, distance: hitComp.distance ?? 0 };
        this.startDragPos = pos.round(1);
        this.startDragTime = Date.now();
        return true;
    }

    boardMouseMove(pos: Point): boolean {
        if (!this.hitComp || !this.startDragTime || !this.startDragPos) {
            return false;
        }
        if (!this.triggeredDrag) {
            if (pos.distance(this.startDragPos) > this.board.pixelRatio * 2) {
                this.triggeredDrag = true;

                if (this.hitComp.hitType == PolygonHitType.Line) {
                    if (!this.onCanAddPoint(this.hitComp.index + 1)) {
                        this.triggeredDrag = false;
                        this.resetMouseProperties(true);
                        return false;
                    }
                    this.hitComp.index++;
                    this.checkNewPointPosition(pos, -1);
                    this.polygon.addPoint(pos.round(1), this.hitComp.index);
                    this.points.splice(this.hitComp.index, -1, pos.scale(1 / this.ratio).round(1));
                    this.hitComp.hitType = PolygonHitType.Dot;
                    this.startDragPos = pos.round(1);
                    this.componentPosOnDragStart = pos.round(1);
                    if (this.onPointAdded) {
                        this.onPointAdded(this.hitComp.index);
                        this.changed = true;
                        this.manuallyEdited = true;
                    }
                    return true;
                } else {
                    this.componentPosOnDragStart = this.polygon.getPoint(this.hitComp.index).round(1);
                }
            }
        }
        if (this.triggeredDrag) {
            const newPoint = pos.sub(this.startDragPos).add(this.componentPosOnDragStart).round(1);
            if (this.checkNewPointPosition(newPoint, this.hitComp.index)) {
                this.polygon.setPoint(this.hitComp.index, newPoint);
                this.points[this.hitComp.index] = newPoint.scale(1 / this.ratio).round(1);
                return true;
            }

            //console.log(`${pos} - ${this.startDragPos} + ${this.componentPosOnDragStart} = ${newPoint}, rescaled: ${this.points[this.hitComp.index]}`);
        }
        return false;
    }

    /**
     * Check and adjust the point position based on the bounds rect. If index > -1 checks if the new point is too close to any existing point
     * @param newPoint Point to check
     * @param index Index of the point
     * @returns True if the point is not too close to any existing point
     */
    checkNewPointPosition(newPoint: Point, index: number): boolean {
        if (newPoint.x < this.boundsRect.left * this.ratio) {
            newPoint.x = this.boundsRect.left * this.ratio;
        } else if (newPoint.x > this.boundsRect.right * this.ratio) {
            newPoint.x = this.boundsRect.right * this.ratio;
        }
        if (newPoint.y < this.boundsRect.top * this.ratio) {
            newPoint.y = this.boundsRect.top * this.ratio;
        } else if (newPoint.y > this.boundsRect.bottom * this.ratio) {
            newPoint.y = this.boundsRect.bottom * this.ratio;
        }
        if (index == -1) {
            return true;
        }
        const translated = newPoint.scale(1 / this.ratio);
        for (let i = 0; i < this.points.length; i++) {
            if (i != index && translated.distance(this.points[i]) <= this.minPointDistance) {
                return false;
            }
        }
        return true;
    }

    boardMouseUp(pos: Point, _e: MouseEvent, _hitElement: CanvasElement | undefined, isClick: boolean): boolean {
        if (!this.hitComp) {
            return false;
        }
        if (isClick && !this.triggeredDrag) {
            if (this.hitComp.hitType == PolygonHitType.Dot) {
                if (this.lastDotClickIndex == this.hitComp.index && this.lastDotClickTime > Date.now() - 500) {
                    // Is double click on a point. Delete it
                    if (!this.onCanDeletePoint || this.onCanDeletePoint(this.hitComp.index)) {
                        this.polygon.removePoint(this.hitComp.index);
                        this.points.splice(this.hitComp.index, 1);
                        if (this.onPointDeleted) {
                            this.onPointDeleted(this.hitComp.index);
                            this.changed = true;
                            this.manuallyEdited = true;
                        }
                    }
                    this.resetMouseProperties(true);
                } else {
                    // Is a click
                    this.lastDotClickIndex = this.hitComp.index;
                    this.lastDotClickTime = this.startDragTime;
                    this.resetMouseProperties(false);
                }
                return true;
            }
        }
        if (this.triggeredDrag) {
            if (this.onFinishedPointEdit) {
                this.manuallyEdited = true;
                this.onFinishedPointEdit(this.hitComp.index);
            }
        }
        this.resetMouseProperties(true);
        return false;
    }
}
